import React, { useState, useEffect } from "react";
import { useSiteContext } from "../../context/SiteContext";

const PollResults = ({ onClose }) => {
    const { pollResults } = useSiteContext();
    const [selectedTab, setSelectedTab] = useState("total");

    useEffect(() => {
        setSelectedTab("total");
    }, [pollResults?.id, pollResults?.lastUpdated]);

    if (!pollResults) {
        return null;
    }

    const totalVotes = pollResults.totalVotes ?? pollResults.votes?.length ?? 0;
    const skippedEntries = pollResults.skipped || [];
    const totalSkipped =
        pollResults.skippedCount ?? skippedEntries.length ?? 0;
    const totalResponses = totalVotes + totalSkipped;

    const getPercentage = (votes) => {
        if (totalVotes === 0) return 0;
        return Math.round((votes / totalVotes) * 100);
    };

    const getVotersForOption = (optionIndex) => {
        if (!pollResults.votes || optionIndex === -1) return [];
        return pollResults.votes.filter((vote) =>
            vote.optionIndices.includes(optionIndex)
        );
    };

    const renderVotersList = () => {
        if (selectedTab === "total") {
            return (
                <div className="space-y-2">
                    {pollResults.votes?.map((vote, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <span className="text-white text-sm">{vote.participantName}</span>
                            <span className="text-gray-400 text-xs">
                                {vote.optionIndices
                                    .map((idx) => pollResults.options[idx]?.text)
                                    .join(", ")}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (selectedTab === "SKIPPED") {
            if (totalSkipped === 0) {
                return (
                    <p className="text-gray-500 text-sm">
                        No participants skipped this poll.
                    </p>
                );
            }

            return (
                <div className="space-y-2">
                    {skippedEntries.map((entry, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <span className="text-white text-sm">
                                {entry.participantName}
                            </span>
                            <span className="text-gray-400 text-xs">Skipped</span>
                        </div>
                    ))}
                </div>
            );
        }

        const optionIndex = [
            "a",
            "b",
            "c",
            "d",
            "e",
            "f",
            "g",
            "h",
            "i",
            "j",
        ].indexOf(selectedTab.toLowerCase());
        const voters = getVotersForOption(optionIndex);

        if (voters.length === 0) {
            return (
                <p className="text-gray-500 text-sm">
                    No votes recorded for this option yet.
                </p>
            );
        }

        return (
            <div className="space-y-2">
                {voters.map((vote, index) => (
                    <div key={index} className="flex items-start gap-2">
                        <span className="text-white text-sm">{vote.participantName}</span>
                        <span className="text-gray-400 text-xs">
                            {pollResults.options[optionIndex]?.text}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
      <div className="fixed inset-0 flex items-center justify-center z-40 p-4">
        <div className="w-80 bg-[#0a0a1f] border border-gray-700 rounded-xl shadow-2xl max-h-[calc(100vh-120px)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h3 className="text-white text-sm font-bold tracking-wider uppercase">
              Poll Control
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Poll Question */}
          <div className="px-4 py-3 border-b border-gray-700">
            <p className="text-white text-sm font-medium">
              {pollResults.question}
            </p>
          </div>

          {/* Options with Results */}
          <div className="px-4 py-3 space-y-2 border-b border-gray-700">
            {pollResults.options?.map((option, index) => {
              const percentage = getPercentage(option.votes);
              const optionLabel = String.fromCharCode(65 + index); // A, B, C, D...

              return (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-gray-400 text-xs font-medium">
                        {optionLabel}
                      </span>
                      <span className="text-white text-xs truncate">
                        {option.text}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-white text-xs font-medium">
                        {option.votes}
                      </span>
                      <span className="text-white text-sm font-bold min-w-[45px] text-right">
                        {percentage}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-[#7700ff] h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>;

          {
            /* Vote Count */
          }
          <div className="px-4 py-3 border-b border-gray-700">
            <p className="text-gray-400 text-xs text-center tracking-wider uppercase">
              Responses: {totalResponses} · Votes: {totalVotes}
              {totalSkipped ? ` · Skipped: ${totalSkipped}` : ""}
            </p>
          </div>;

          {
            /* Tabs */
          }
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setSelectedTab("total")}
                className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                  selectedTab === "total"
                    ? "bg-[#7700ff] text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                }`}
              >
                TOTAL
              </button>
              {pollResults.options?.map((_, index) => {
                const optionLabel = String.fromCharCode(65 + index);
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedTab(optionLabel)}
                    className={`w-10 h-10 rounded-lg text-xs font-medium transition-colors ${
                      selectedTab === optionLabel
                        ? "bg-[#7700ff] text-white"
                        : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                  >
                    {optionLabel}
                  </button>
                );
              })}
              {totalSkipped > 0 && (
                <button
                  onClick={() => setSelectedTab("SKIPPED")}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                    selectedTab === "SKIPPED"
                      ? "bg-[#7700ff] text-white"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                  }`}
                >
                  SKIPPED
                </button>
              )}
            </div>
          </div>;

          {/* Voters Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-3">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">
                Voters
              </p>
              {totalResponses === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">
                  No responses yet
                </p>
              ) : (
                renderVotersList()
              )}
            </div>
          </div>
        </div>
      </div>
    );
};

export default PollResults;

